// src/auth/authSlice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  socialLogin,
  authMe,
  verifyEmail,
  login,
  register,
  logout,
} from "./authThunks";
import {
  updateUserSettings,
  updatePassword,
  fetchUserData,
} from "./updateUserThunks";

import { initialState } from "./authTypes";
import { FilterValues } from "../types/ProductsTypes";
import { currencyMapping } from "utils/currencyMaping";
import { setAccessToken } from "../../services/tokenManager";

const STEAM_PLACEHOLDER_DOMAINS = [
  "users.steam.bigswiss.com",
  "steam.local",
];

// Utility function
function isFilterKey(key: any): key is keyof FilterValues {
  return ["category", "platform", "type", "sort", "genre", "maxPrice"].includes(
    key,
  );
}

const hasIncompleteBillingInfo = (user: any | null): boolean => {
  if (!user) return true;

  const missingName =
    !user.firstName ||
    user.firstName === "Unknown" ||
    !user.lastName ||
    user.lastName === "Unknown";

  const missingAddress =
    !user.address || !user.city || !user.zip || !user.country || !user.birthday;

  const placeholderEmail =
    !user.email ||
    (typeof user.email === "string" &&
      STEAM_PLACEHOLDER_DOMAINS.some((domain) =>
        user.email.toLowerCase().endsWith(`@${domain}`),
      ));

  return missingName || missingAddress || placeholderEmail;
};

export const setSearchQuery = (query: string) => ({
  type: "SET_SEARCH_QUERY",
  payload: query,
});
interface VerificationPayload {
  verificationSecret: string | null;
  verificationError: string | null;
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthState: (state) => {
      state.user = null;
      state.token = undefined;
      state.isLoggedIn = false;
      state.isSocialLoginLoading = false;
      state.hasMissingInfo = false;
      state.isEmailVerified = false;
      state.identityRequireCheck = false;
      state.welcomePageLoading = false;
      state.error = null;
      state.verificationSecret = null;
      state.verificationError = null;
      state.verificationStatus = "processing_verification";
      state.mainLayoutFade = false;
      state.activePurchase = false;
      state.pendingReferralCode = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setMainLayoutFade: (state) => {
      state.mainLayoutFade = true;
    },
    setNewVerificationAction: (
      state,
      action: PayloadAction<VerificationPayload>,
    ) => {
      const { verificationSecret, verificationError } = action.payload;
      state.verificationSecret = verificationSecret;
      state.verificationError = verificationError;
    },
    setVerificationStatus: (state, action: PayloadAction<string>) => {
      state.verificationStatus = action.payload;
    },
    setVerificationAction: (
      state,
      action: PayloadAction<VerificationPayload>,
    ) => {
      const { verificationSecret, verificationError } = action.payload;
      state.verificationSecret = verificationSecret;
      state.verificationError = verificationError;
      // Additional flags
      state.identityRequireCheck = true;
      state.activePurchase = true;
      state.mainLayoutFade = false;
    },
    setWelcomePageLoading: (state, action: PayloadAction<boolean>) => {
      state.welcomePageLoading = action.payload;
    },
    setUpdateIdentityRequireCheck: (state, action: PayloadAction<boolean>) => {
      state.identityRequireCheck = action.payload;
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload;
    },
    setFilter: (
      state,
      action: PayloadAction<{ key: keyof FilterValues; value: string }>,
    ) => {
      const { key, value } = action.payload;
      if (isFilterKey(key)) {
        state.filter[key] = value;
      }
    },
    setOnlineStatus(state, action: PayloadAction<boolean>) {
      state.isOnline = action.payload;
    },
    setSelectedCurrency(state, action: PayloadAction<string>) {
      state.selectedCurrency = action.payload;
    },
    setSelectedCurrencySymbol(state, action: PayloadAction<string>) {
      state.selectedCurrencySymbol = action.payload;
    },
    setCurrencyAndRegion(
      state,
      action: PayloadAction<{ currency: string; region: string }>,
    ) {
      state.selectedCurrency = action.payload.currency;
      state.selectedRegion = action.payload.region;
      // Derive symbol from central currencyMapping helper
      const mappingEntry = currencyMapping[action.payload.currency];
      if (mappingEntry) {
        const sym = mappingEntry.symbol;
        // When symbol is a string (e.g. "€" or "CHF") store it directly;
        // when it's an icon component, fall back to the ISO code so the UI can decide how to render.
        state.selectedCurrencySymbol =
          typeof sym === "string" ? (sym as string) : action.payload.currency;
      } else {
        // Fallback: default to Euro symbol
        state.selectedCurrencySymbol = "€";
      }

      const germanSpeaking = ["DE", "AT", "CH", "LI", "LU"];
      const frenchSpeaking = ["FR", "BE", "CA"];
      const italianSpeaking = ["IT", "SM", "VA"];

      if (germanSpeaking.includes(action.payload.region)) {
        state.language = "de";
      } else if (frenchSpeaking.includes(action.payload.region)) {
        state.language = "fr";
      } else if (italianSpeaking.includes(action.payload.region)) {
        state.language = "it";
      } else {
        state.language = "en";
      }
    },
    setUserIp(state, action: PayloadAction<string>) {
      state.userIp = action.payload;
    },
    resetFilters: (state) => {
      state.filter = {
        platform: "",
        maxPrice: "",
        type: "",
        category: "",
        genre: "",
        sort: "A-Z",
      };
    },
    setPendingReferralCode: (state, action: PayloadAction<string | null>) => {
      state.pendingReferralCode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // -- login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        const user = action.payload.user;
        const token = action.payload.token || action.payload.accessToken;
        state.isLoading = false;
        state.isLoggedIn = true;
        state.user = user;
        state.token = token;
        state.error = null;
        state.isEmailVerified = action.payload.emailVerified;
        state.identityRequireCheck = action.payload.identityRequireCheck;
        state.hasMissingInfo = hasIncompleteBillingInfo(user);

        state.welcomePageLoading =
          !state.isEmailVerified ||
          state.hasMissingInfo ||
          state.identityRequireCheck;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // -- socialLogin
      .addCase(socialLogin.pending, (state) => {
        state.isLoading = true;
        state.isSocialLoginLoading = false;
        state.error = null;
      })
      .addCase(socialLogin.fulfilled, (state) => {
        state.isLoading = false;
        state.isLoggedIn = true;
        state.isSocialLoginLoading = true;
        state.isEmailVerified = true;
        state.error = null;
      })
      .addCase(socialLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.isSocialLoginLoading = false;
        state.error = action.payload as string;
      })

      // -- authMe
      .addCase(authMe.pending, (state) => {
        state.isSocialLoginLoading = true;
        state.error = null;
      })
      .addCase(authMe.fulfilled, (state, action) => {
        const user = action.payload.user;
        const token = action.payload.token ?? (action.payload as any).accessToken ?? null;
        state.isLoading = false;
        state.isLoggedIn = true;
        state.isSocialLoginLoading = false;
        state.isEmailVerified = true;
        state.user = user;
        if (token) {
          state.token = token;
          setAccessToken(token);
        }
        state.identityRequireCheck = action.payload.identityRequireCheck;
        state.hasMissingInfo =
          !user.firstName ||
          user.firstName === "Unknown" ||
          !user.lastName ||
          user.lastName === "Unknown" ||
          !user.birthday ||
          !user.address ||
          !user.city ||
          !user.zip ||
          !user.country;

        state.welcomePageLoading =
          !state.isEmailVerified ||
          state.hasMissingInfo ||
          state.identityRequireCheck;
        state.error = null;
      })
      .addCase(authMe.rejected, (state) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        state.isSocialLoginLoading = false;
        state.user = null;
        state.identityRequireCheck = false;
        state.welcomePageLoading = false;
        state.error = null;
      })

      // -- verifyEmail
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.isLoading = false;
        state.isEmailVerified = true;
        state.error = null;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // -- logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isLoggedIn = false;
        state.isSocialLoginLoading = false;
        state.token = undefined;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // -- register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
        state.pendingReferralCode = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = { ...state.user, ...action.payload };
        state.hasMissingInfo = hasIncompleteBillingInfo(state.user);
        state.error = null;
      })
      .addCase(updateUserSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.isLoggedIn = true;
        state.user = action.payload;
        state.hasMissingInfo = hasIncompleteBillingInfo(state.user);
      })
      .addCase(updatePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearAuthState,
  clearError,
  setWelcomePageLoading,
  setUpdateIdentityRequireCheck,
  setMainLayoutFade,
  setVerificationAction,
  setNewVerificationAction,
  setVerificationStatus,
  setOnlineStatus,
  setCurrencyAndRegion,
  setUserIp,
  setLanguage,
  setFilter,
  resetFilters,
  setPendingReferralCode,
  setSelectedCurrency,
  setSelectedCurrencySymbol,
} = authSlice.actions;

export default authSlice.reducer;
